import {
  getAccessControlledAppKeys,
  getHubMarketplaceEntries,
  resolveAppKeyFromRoute,
} from "../../app-catalog/index.js";
import {
  canAccessAccessRequestsAdmin,
  canAccessApp as canAccessAppCore,
  canAccessAppWithEntitlements as canAccessAppWithEntitlementsCore,
  canAccessAppWithEntitlementsAndRoleAppAccess as canAccessAppWithEntitlementsAndRoleAppAccessCore,
  deriveAllowedAppKeysFromBindingsAndEntitlements as deriveAllowedAppKeysFromBindingsAndEntitlementsCore,
  deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccess as deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccessCore,
  deriveAllowedAppKeysFromRoleBindings as deriveAllowedAppKeysFromRoleBindingsCore,
  deriveAllowedAppKeysFromRoleBindingsAndRoleAppAccess as deriveAllowedAppKeysFromRoleBindingsAndRoleAppAccessCore,
  deriveEffectiveMarketplaceAppsForMemberRoles as deriveEffectiveMarketplaceAppsForMemberRolesCore,
  deriveEffectiveMemberMarketplaceApps as deriveEffectiveMemberMarketplaceAppsCore,
  deriveGovernedMarketplaceAppKeys as deriveGovernedMarketplaceAppKeysCore,
  deriveScopeAvailableAppKeysWithCatalog as deriveScopeAvailableAppKeysCore,
  getEffectiveVisibleAppsForScopeWithCatalog as getEffectiveVisibleAppsForScopeCore,
  hasActiveTenantRoleBindings,
  isGlobalSuperadminForSession,
  isGlobalSuperadminOnlyRoute,
  normalizeGovernanceAppKey,
  resolveRoleAppAccessEffectiveAtGlobal,
  resolveRoleAppAccessEffectiveAtTenant,
  resolveRoleAppAccessMatrixCell,
  restrictEffectiveVisibleAppsForFamilyOnly,
  shouldShowInTenantRoleMatrix,
} from "@tennis-matches/services/governance";

export {
  isGlobalSuperadminForSession,
  isGlobalSuperadminOnlyRoute,
  normalizeGovernanceAppKey,
  hasActiveTenantRoleBindings,
  resolveRoleAppAccessMatrixCell,
  resolveRoleAppAccessEffectiveAtTenant,
  resolveRoleAppAccessEffectiveAtGlobal,
  shouldShowInTenantRoleMatrix,
  canAccessAccessRequestsAdmin,
};

export const ALL_APP_KEYS_PHASE1 = getAccessControlledAppKeys();

export function getHubMarketplaceAppKeySet() {
  return new Set(
    getHubMarketplaceEntries()
      .map((e) => normalizeGovernanceAppKey(e?.appKey))
      .filter(Boolean),
  );
}

function __catalogContext() {
  return {
    accessControlledAppKeys: ALL_APP_KEYS_PHASE1,
    marketplaceAppKeys: Array.from(getHubMarketplaceAppKeySet()),
  };
}

function __hubMarketplaceOnlyPhase1ExcludedKeys() {
  const out = new Set();
  for (const entry of getHubMarketplaceEntries()) {
    if (entry?.inAllAppKeysPhase1 === false) {
      const k = normalizeGovernanceAppKey(entry?.appKey);
      if (k) out.add(k);
    }
  }
  return out;
}

function __extraGlobalScopeAppKeysFromCatalog() {
  const keys = [];
  for (const entry of getHubMarketplaceEntries()) {
    if (entry?.inAllAppKeysPhase1 === false) continue;
    const appKey = normalizeGovernanceAppKey(entry?.appKey);
    if (appKey) keys.push(appKey);
  }
  return keys;
}

export function deriveGovernedMarketplaceAppKeys(
  roleBindings,
  roleAppAccessRows,
  authUser = null,
) {
  return deriveGovernedMarketplaceAppKeysCore(
    roleBindings,
    roleAppAccessRows,
    authUser,
    __catalogContext(),
  );
}

export function deriveAllowedAppKeysFromRoleBindings(
  roleBindings,
  authUser = null,
) {
  return deriveAllowedAppKeysFromRoleBindingsCore(
    roleBindings,
    authUser,
    __catalogContext(),
  );
}

export function deriveAllowedAppKeysFromRoleBindingsAndRoleAppAccess(
  roleBindings,
  roleAppAccessRows,
  authUser = null,
) {
  return deriveAllowedAppKeysFromRoleBindingsAndRoleAppAccessCore(
    roleBindings,
    roleAppAccessRows,
    authUser,
    __catalogContext(),
  );
}

export function deriveEffectiveMarketplaceAppsForMemberRoles(
  memberRoles,
  tenantScopeId,
  roleAppAccessRows,
) {
  return deriveEffectiveMarketplaceAppsForMemberRolesCore(
    memberRoles,
    tenantScopeId,
    roleAppAccessRows,
    __catalogContext(),
  );
}

export function deriveEffectiveMemberMarketplaceApps(
  memberRoles,
  tenantScopeId,
  roleAppAccessRows,
  userEntitlements = [],
) {
  return deriveEffectiveMemberMarketplaceAppsCore(
    memberRoles,
    tenantScopeId,
    roleAppAccessRows,
    userEntitlements,
    __catalogContext(),
  );
}

export function deriveAllowedAppKeysFromBindingsAndEntitlements(
  roleBindings,
  entitlements,
  authUser = null,
) {
  return deriveAllowedAppKeysFromBindingsAndEntitlementsCore(
    roleBindings,
    entitlements,
    authUser,
    __catalogContext(),
  );
}

export function deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccess(
  roleBindings,
  entitlements,
  roleAppAccessRows,
  authUser = null,
) {
  return deriveAllowedAppKeysFromBindingsEntitlementsAndRoleAppAccessCore(
    roleBindings,
    entitlements,
    roleAppAccessRows,
    authUser,
    __catalogContext(),
  );
}

export function canAccessApp(appKey, roleBindings, authUser = null) {
  return canAccessAppCore(appKey, roleBindings, authUser, __catalogContext());
}

export function canAccessAppWithEntitlements(
  appKey,
  roleBindings,
  entitlements,
  authUser = null,
) {
  return canAccessAppWithEntitlementsCore(
    appKey,
    roleBindings,
    entitlements,
    authUser,
    __catalogContext(),
  );
}

export function canAccessAppWithEntitlementsAndRoleAppAccess(
  appKey,
  roleBindings,
  entitlements,
  roleAppAccessRows,
  authUser = null,
) {
  return canAccessAppWithEntitlementsAndRoleAppAccessCore(
    appKey,
    roleBindings,
    entitlements,
    roleAppAccessRows,
    authUser,
    __catalogContext(),
  );
}

export function deriveScopeAvailableAppKeys(opts = {}) {
  return deriveScopeAvailableAppKeysCore({
    ...opts,
    catalogContext: __catalogContext(),
    extraGlobalScopeAppKeys: __extraGlobalScopeAppKeysFromCatalog(),
  });
}

export function getEffectiveVisibleAppsForScope(opts = {}) {
  const base = getEffectiveVisibleAppsForScopeCore({
    ...opts,
    catalogContext: __catalogContext(),
    extraGlobalScopeAppKeys: __extraGlobalScopeAppKeysFromCatalog(),
  });
  if (base?.loaded !== true) return base;

  const marketplace = getHubMarketplaceAppKeySet();
  const marketplaceOnlyExcluded = __hubMarketplaceOnlyPhase1ExcludedKeys();
  const effective = new Set(base.effectiveVisibleAppKeys || []);
  const userAllowedRaw = base.userAllowedAppKeys || new Set();
  const isFeatureEnabled = opts.isFeatureEnabled;

  for (const appKey of userAllowedRaw) {
    if (!marketplaceOnlyExcluded.has(appKey)) continue;
    if (!marketplace.has(appKey)) continue;
    const enabled =
      typeof isFeatureEnabled === "function"
        ? isFeatureEnabled(appKey) !== false
        : true;
    if (!enabled) continue;
    effective.add(appKey);
  }

  return {
    ...base,
    effectiveVisibleAppKeys: restrictEffectiveVisibleAppsForFamilyOnly(
      effective,
      opts.roleBindings,
    ),
  };
}

/** App-only: Rollen-&-Apps-Matrix — Phase-1-Katalog ∩ Feature-Flags ∩ Manifeste. */
export function deriveRoleMatrixConfigurableAppKeys({
  appManifests,
  isLoaded = false,
  isFeatureEnabled = null,
} = {}) {
  const out = new Set();
  const addIfEnabled = (appKey) => {
    const k = normalizeGovernanceAppKey(appKey);
    if (!k) return;
    const enabled =
      typeof isFeatureEnabled === "function"
        ? isFeatureEnabled(k) !== false
        : true;
    if (enabled) out.add(k);
  };
  for (const appKey of ALL_APP_KEYS_PHASE1) addIfEnabled(appKey);
  for (const entry of getHubMarketplaceEntries()) addIfEnabled(entry?.appKey);
  if (isLoaded && Array.isArray(appManifests)) {
    for (const row of appManifests) {
      const k = normalizeGovernanceAppKey(row?.id || row?.app_id);
      if (k) addIfEnabled(k);
    }
  }
  return { ok: true, loaded: isLoaded, availableAppKeys: out };
}

function __trainerAllowsGovernanceAppKey(trainerAllowedAppKeys, appKey) {
  const allowed =
    trainerAllowedAppKeys instanceof Set
      ? [...trainerAllowedAppKeys]
      : Array.isArray(trainerAllowedAppKeys)
        ? trainerAllowedAppKeys
        : [];
  const normalized = normalizeGovernanceAppKey(appKey);
  if (!normalized) return false;
  return allowed.some((k) => normalizeGovernanceAppKey(k) === normalized);
}

function __isTrainingsDeskCoreAppKey(appKey) {
  const k = normalizeGovernanceAppKey(appKey);
  return k === "trainerstunden" || k === "trainingsdesk" || k === "coachdesk";
}

function __isOpenPhase1TournamentAppRoute(routeKey) {
  const appKey = normalizeGovernanceAppKey(
    resolveAppKeyFromRoute(routeKey) || "",
  );
  return (
    appKey === normalizeGovernanceAppKey("matchmixer") ||
    appKey === normalizeGovernanceAppKey("clubmeisterschaft")
  );
}

/** App-only: Route-Gate via App-Katalog (resolveAppKeyFromRoute). */
export function canAccessRouteApp(route, ctx = {}) {
  const r = String(route || "").trim();
  if (r === "trainingsdesk-invite") return true;
  if (r === "matchflow-accept") return true;
  if (r.startsWith("hub-")) return true;
  if (__isOpenPhase1TournamentAppRoute(r)) return true;

  const rawKey = resolveAppKeyFromRoute(r);
  const appKey = rawKey ? normalizeGovernanceAppKey(rawKey) : "";
  if (!appKey) return true;

  if (!ctx.accessReady) return false;

  // Superadmin: Shell nie mit „Kein Zugriff“ blocken (Feature-Gates in-app).
  if (ctx.isGlobalSuperAdmin) return true;

  // TrainingsDesk-Kern (Starter/Free/Expired): App immer laden.
  // Feature-Limits (Billing/Analytics/Club-Apps) sitzen in der App selbst.
  if (__isTrainingsDeskCoreAppKey(appKey)) return true;

  const visible = ctx.effectiveVisibleAppKeys;
  const inVisible = visible instanceof Set && visible.has(appKey);
  if (!inVisible) return false;

  if (ctx.isTrainerLogin) {
    return __trainerAllowsGovernanceAppKey(ctx.trainerAllowedAppKeys, appKey);
  }

  return true;
}
